import {
  HStack,
  Icon,
  IconButton,
  StackProps,
  Textarea,
} from "@chakra-ui/react";
import { RiSendPlane2Line } from "@remixicon/react";
import { useFormik } from "formik";
import { useState } from "react";
import {
  ProductChatInterface,
  ProductParamInterface,
} from "../../constants/Chat";
import useCustomToast from "../../lib/useCustomToast";
import { useGetUserLoginStore } from "../../store/useGetUserLoginStore";
import { useUserHeader } from "../../store/useUserHeader";
import { useBgHover, useDarkWhiteColor } from "../../themes/colors";
import { getAxiosInstance, isUuid } from "../../utils/helperFunction";

interface Props extends StackProps {
  userId: any;
  selectedChat: string | number | undefined;
  productChat: ProductChatInterface | undefined;
  productParam?: ProductParamInterface | undefined;
  onSendMessage: (id_chat: any) => void;
}

const initialValues = {
  message: undefined,
};

export const InputMessage = ({
  userId,
  selectedChat,
  productChat,
  productParam,
  onSendMessage,
  ...rest
}: Props) => {
  const [sendLoading, setSendLoading] = useState<boolean>(false);
  const { currentActiveUser } = useGetUserLoginStore();
  const toast = useCustomToast();
  const bgBnw = useDarkWhiteColor();
  const bgHover = useBgHover();
  const { userHeader } = useUserHeader();

  const formik = useFormik({
    validateOnChange: false,
    validateOnBlur: false,
    initialValues: initialValues,
    onSubmit: (values, { resetForm }) => {
      if (!userHeader?.name) {
        return toast({
          title:
            "Lengkapi profil pembeli anda terlebih dahulu untuk menggunakan fitur chat",
        });
      }

      if (!values.message || (values.message as string).trim() === "") {
        return toast({
          title: "Ketik pesan dulu",
          duration: 1000,
        });
      }

      if ((values.message as string).length > 150) {
        return toast({
          title: "Pesan tidak boleh lebih dari 150 karakter",
          duration: 1000,
        });
      }

      if (currentActiveUser) {
        let pageType = 1; //buyer

        const payload = {
          id_user_sender: currentActiveUser.id,
          sender_user_type: currentActiveUser.role,
          id_user_receiver: userId,
          content: values.message,
          ...(productChat &&
            productParam && {
              id_product: productParam.productId,
              id_variant: productParam.variantId,
              storePrefix: productParam.storePrefix,
            }),
          ...(isUuid(selectedChat as string) && { id_chat: selectedChat }),
        };

        if (currentActiveUser?.role === "supplier") {
          pageType = 3;
        }

        setSendLoading(true);

        const axiosInstance = getAxiosInstance(pageType);
        axiosInstance
          .post(`v1/chat/send-message`, payload)
          .then((res) => {
            resetForm({ values: initialValues });

            onSendMessage(res.data.data.id_chat);
          })
          .catch((err) => {
            toast({
              description: err?.response?.data?.message || "",
            });
          })
          .finally(() => {
            setSendLoading(false);
          });
      } else {
        setSendLoading(false);
      }
    },
  });

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      e.key === "Enter" &&
      !e.shiftKey &&
      !/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      )
    ) {
      e.preventDefault();
      formik.handleSubmit();
    }
  };

  return (
    <HStack
      w={"100%"}
      px={{ base: 4, md: 2 }}
      py={2}
      bg={bgBnw}
      position={{ base: "fixed", md: "sticky" }}
      bottom={0}
      {...rest}
    >
      <form
        id="messageForm"
        onSubmit={formik.handleSubmit}
        style={{ width: "100%" }}
      >
        <HStack w={"100%"} spacing={{ base: 2, md: 1 }}>
          <Textarea
            padding={{ base: 3, md: 2 }}
            name="message"
            onChange={formik.handleChange}
            onKeyDown={handleKeyDown}
            resize="none"
            rows={1}
            borderRadius={{ base: "lg", md: "md" }}
            autoComplete="off"
            value={formik.values.message || ""}
            placeholder="Ketik pesan..."
            _placeholder={{ size: "md" }}
            size={{ base: "md", md: "sm" }}
            bg={bgHover}
            isDisabled={sendLoading ? true : false}
          />

          <IconButton
            form="messageForm"
            type="submit"
            aria-label="send"
            size="md"
            isDisabled={sendLoading ? true : false}
            borderRadius={{ base: "full", md: "md" }}
            icon={<Icon as={RiSendPlane2Line} boxSize="20px" />}
            colorScheme="red"
          />
        </HStack>
      </form>
    </HStack>
  );
};
